import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, FingerPrintIcon, QrCodeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FaApple, FaTelegramPlane } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import logo from "../assets/images/logo.png";
import Post from "../services/Post";
import { ThemeContext } from "../components/ThemeContext";
import { useSiteConfig } from "../components/SiteConfigContext";
import { loginWithPasskey, passkeysSupported } from "../services/passkey";
import "./Login.css";

const Login = () => {
  const { socialLogin } = useSiteConfig();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("identifier");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [alternateMessage, setAlternateMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const identifierIsPhone = /^[+\d][\d\s().-]*$/.test(email.trim()) && /\d/.test(email);
  const normalizedIdentifier = identifierIsPhone
    ? email.trim().startsWith("+") ? `+${email.trim().replace(/\D/g, "")}` : `${countryCode}${email.trim().replace(/\D/g, "")}`
    : email.trim();

  useEffect(() => {
    document.title = "Login | TillionX";
  }, []);

  const finishLogin = (response) => {
    const responseData = response?.data || response;
    const payload = responseData?.data || responseData;
    const token = payload?.token || responseData?.token || response?.token;
    if (!token) {
      setError("Login response did not include an access token.");
      return;
    }
    localStorage.setItem("token", token);
    const storedUser = { ...payload };
    delete storedUser.token;
    localStorage.setItem("user", JSON.stringify(storedUser));
    setSuccess("Login successful! Redirecting you now...");
    window.setTimeout(() => navigate("/home"), 1000);
  };

  const handlePasskeyLogin = () => {
    setError("");
    setAlternateMessage("");
    setLoading(true);
    loginWithPasskey()
      .then(finishLogin)
      .catch((err) => setError(err.message || "Passkey sign-in failed."))
      .finally(() => setLoading(false));
  };

  const login = (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!normalizedIdentifier) {
      setError("Enter your email or phone number to continue.");
      return;
    }

    if (step === "identifier") {
      setStep("password");
      return;
    }
    if (!password) {
      setError("Enter your password to continue.");
      return;
    }

    setLoading(true);

    Post.Login({ email: normalizedIdentifier, password })
      .then((response) => {
        const responseData = response?.data || response;
        const payload = responseData?.data || responseData;
        const token = payload?.token || responseData?.token || response?.token;
        const user = payload?.user || responseData?.user || (payload?.id ? payload : null);
        if (!token) {
          setError("Login response did not include an access token.");
          return;
        }
        localStorage.setItem("token", token);
        if (user) {
          const storedUser = { ...user };
          delete storedUser.token;
          localStorage.setItem("user", JSON.stringify(storedUser));
        }
        setSuccess("Login successful! Redirecting you now...");
        window.setTimeout(() => navigate("/home"), 1000);
      })  
      .catch((err) => {
        const message = err.message || "Unable to log in. Please try again.";
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <main className={`login-page ${theme === "light" ? "login-light" : ""}`}>
      <section className={`login-card ${step === "identifier" ? "login-identifier-mode" : ""}`} aria-label="Log in to TillionX">
        <Link className="login-brand" to="/" aria-label="TillionX home"><img src={logo} alt="TillionX" /></Link>
        <div className="login-title-row">
          {step === "password" && <button className="login-back" type="button" aria-label="Back to email or phone" onClick={() => { setStep("identifier"); setError(""); }}><ArrowLeftIcon /></button>}
          <h1>Log in</h1>
          {step === "identifier" && <button className="login-qr-button" type="button" aria-label="QR sign-in" title="QR sign-in" onClick={() => setAlternateMessage("QR sign-in isn't available yet.")}><QrCodeIcon /></button>}
        </div>
        <form onSubmit={login}>
          {step === "identifier" ? <div className="login-field">
            <label htmlFor="login-identifier">Email/Phone number</label>
            <div className={`login-identifier-input ${identifierIsPhone ? "is-phone" : ""}`}>
              {identifierIsPhone && <select aria-label="Country calling code" value={countryCode} onChange={(event) => setCountryCode(event.target.value)}>
                <option value="+91">🇮🇳 +91</option>
                <option value="+1">🇺🇸 +1</option>
                <option value="+44">🇬🇧 +44</option>
                <option value="+61">🇦🇺 +61</option>
                <option value="+86">🇨🇳 +86</option>
                <option value="+81">🇯🇵 +81</option>
                <option value="+971">🇦🇪 +971</option>
              </select>}
              <div className="login-phone-input">
                <input id="login-identifier" type="text" autoComplete="username" placeholder="Email or phone number" value={email} onChange={(event) => { setEmail(event.target.value); setAlternateMessage(""); }} />
                {email && <button className="login-clear" type="button" aria-label="Clear email or phone number" onClick={() => { setEmail(""); setAlternateMessage(""); }}><XMarkIcon /></button>}
              </div>
            </div>
          </div> : <>
            <p className="login-identifier-value">{normalizedIdentifier}</p>
            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <div className="login-password-wrap"><input id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="Password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /><button type="button" className="login-eye" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword((v) => !v)}>{showPassword ? <FiEyeOff /> : <FiEye />}</button></div>
            </div>
            <Link className="login-forgot" to="/reset-password">Forgot password?</Link>
          </>}
          {error && <p role="alert" className="login-error">{error}</p>}
          {success && <p role="status" className="login-success">{success}</p>}
          <button type="submit" className="login-submit" disabled={loading}>{loading ? "Logging in..." : "Log in"}</button>
        </form>
        <div className="login-divider"><span>or</span></div>
        {step === "identifier" && <div className="login-alternates">
          {socialLogin.passkey !== false && passkeysSupported() && <button type="button" onClick={handlePasskeyLogin} disabled={loading}><FingerPrintIcon /><span>Continue with Passkey</span></button>}
          {socialLogin.google && <button type="button" onClick={() => setAlternateMessage("Google sign-in isn't available yet.")}><FcGoogle /><span>Continue with Google</span></button>}
          {socialLogin.apple && <button type="button" onClick={() => setAlternateMessage("Apple sign-in isn't available yet.")}><FaApple /><span>Continue with Apple</span></button>}
          {socialLogin.telegram && <button type="button" onClick={() => setAlternateMessage("Telegram sign-in isn't available yet.")}><FaTelegramPlane /><span>Continue with Telegram</span></button>}
          {alternateMessage && <p role="status">{alternateMessage}</p>}
        </div>}
        <div className="login-footer"><Link to="/signup">Create an account</Link>{step === "identifier" && <Link to="/reset-password">Forgot password?</Link>}</div>
      </section>
    </main>
  );
};

export default Login;
